// FleetRevenuePage — what each customer is billed, beside what they cost us.
//
// A metricsIQ screen beside Fleet Costs. Billing and Metering exist inside one
// customer's scope; this one reads every customer's over one range of days and
// totals them, from GET /portal/api/metricsiq/fleet-revenue/, which answers a
// staff token and 404s everybody else.
//
// ONE SOURCE PER FIGURE, and this screen draws it rather than recomputing it.
// The amount beside a customer is the figure their own Billing page shows for
// the same days, from the same function on the service; the cost is the figure
// Fleet Costs shows for the same range, summed over the customer's instances;
// the margin is the service's subtraction. Nothing here adds, subtracts or
// rounds — every money figure is `money()`'d from the decimal string it arrived
// as, two places, as every page of this portal shows money.
//
// EVERY AMOUNT IS AN ESTIMATE AND SAYS SO, in the Billing page's own words and
// tone: this service is not the invoicing authority, a channel is. The pending
// figure is weaker still — the exact ledger quantity priced for hours the
// channel has not answered — and is kept in its own column rather than folded
// into the amount.
//
// NOT BILLABLE IS NEVER ZERO. A customer on a channel with no adapter —
// `internal`, `seeded` — reads "Not billable" in muted text where a figure
// would be. A zero would be a claim that they owed nothing; the truth is that
// nobody is asking them for money. The service keeps the two apart on the wire
// (`billable: false`, `amount: null`) and this screen draws what it was told.
//
// Design-system components by module path, as every page here, and DS button
// handlers are `onPress`. Styling is role tokens only.

import { useCallback, useState } from 'react';
import { ArrowUpRight, CreditCard, Download } from 'lucide-react';
import { Badge } from '@miq/design-system/components/ui/badge';
import { Button } from '@miq/design-system/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';

import type { AuthorizedFetch } from '../auth/api';
import { PortalRequestFailed } from '../auth/api';
import { usePortalSession } from '../auth/context';
import { DayRangePicker } from '../components/DayRangePicker';
import { ShowFormerToggle } from '../components/ShowFormerToggle';
import { StatusCell } from '../components/StatusCell';
import {
  downloadFleetRevenueExport,
  readFleetRevenue,
  type DayRangeQuery,
  type FleetRevenueAmount,
  type FleetRevenueCarry,
  type FleetRevenueWithheld,
  type FleetRevenueCost,
  type FleetRevenueCustomer,
  type FleetRevenuePending,
  type FleetRevenueScreen,
  type FleetRevenueSettlement,
  type FleetRevenueTotal,
} from '../data/acrossCustomers';
import { ABSENT, countText, dayRangeLabel, quantityText } from '../data/format';
import { money } from '../format';
import { usePortalRead } from '../data/usePortalRead';
import { useDocumentTitle } from '../../lib/documentTitle';
import { ACCRUING_TONE, DISCLOSURE_TONE } from './BillingPage';

const TITLE = 'Fleet Revenue';

/** The currency the cost ledger is kept in; the service states it in `usd`. */
const COST_CURRENCY = 'USD';

/** How each settlement state reads. The service's word, in the reader's. */
const STATE_LABEL: Record<FleetRevenueSettlement['state'], string> = {
  settled: 'Settled',
  pending: 'Pending',
  withheld: 'Withheld',
  carried_forward: 'Carried forward',
  not_billable: 'Not billable',
};

export function FleetRevenuePage({
  onOpenCustomerBilling,
}: {
  /**
   * Take this session into the customer's own scope and open their Billing
   * page, so the figure on this row can be checked against its source.
   */
  onOpenCustomerBilling: (publicId: string) => void;
}) {
  useDocumentTitle(TITLE);
  // `null` means "did not ask", which the service reads as the month to date —
  // see DayRangePicker for why the control keeps it that way.
  const [range, setRange] = useState<DayRangeQuery | null>(null);
  // Former customers — retired or superseded — are hidden by default and come
  // back under the toggle, memoised alongside the range for the same reason
  // the range is.
  const [showFormer, setShowFormer] = useState(false);

  const read = useCallback(
    (authorizedFetch: AuthorizedFetch) => readFleetRevenue(authorizedFetch, range, showFormer),
    [range, showFormer],
  );
  const screen = usePortalRead(read);
  const hiddenCount =
    screen.status !== 'ready'
      ? 0
      : showFormer
        ? screen.data.customers.filter((row) => row.retired !== null || row.superseded_by !== null).length
        : screen.data.former_hidden;

  // The header, the range control and the disclosure are rendered in every
  // state, as Fleet Costs renders its header: a refused range is answered 400
  // and the control that produced it has to stay on screen to be corrected.
  return (
    <div className="space-y-5 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Commerce · metricsIQ
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{TITLE}</h1>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            What each customer is billed for the range chosen here — the figure
            their own Billing page shows for the same days — beside what their
            instances cost us over the same days, and the margin between. One
            row per customer, a total at the foot, and an export of the priced
            lines.
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <DayRangePicker
            selected={range}
            onSelect={setRange}
            range={screen.status === 'ready' ? screen.data.range : null}
          />
          <ShowFormerToggle showing={showFormer} hiddenCount={hiddenCount} onChange={setShowFormer} of="customers" />
        </div>
      </div>

      {/* The disclosure, in the Billing page's own words and tone: it is the
          difference between a page of estimates and a page of charges, and a
          colleague reading these figures needs it as much as a customer does. */}
      <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${DISCLOSURE_TONE}`}>
        <CreditCard className="mt-0.5 size-4 shrink-0" />
        <p className="text-xs leading-relaxed">
          <span className="font-semibold">Every amount below is an estimate.</span>{' '}
          The quantities are what our metering ledger recorded and handed to each
          customer&apos;s sales channel, priced at their agreement&apos;s rates. The
          channel is the authority for what each customer is invoiced, and applies
          its own rounding, billing calendar and tax. A pending figure is weaker
          still: it prices hours the channel has not yet answered for.
        </p>
      </div>

      {screen.status === 'loading' && (
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-muted-foreground" role="status">
              Loading…
            </p>
          </CardContent>
        </Card>
      )}
      {screen.status === 'failed' && (
        <Card>
          <CardContent className="py-8">
            <p className="text-sm text-muted-foreground" role="alert">
              {screen.message}
            </p>
          </CardContent>
        </Card>
      )}
      {screen.status === 'ready' && (
        <FleetRevenue
          screen={screen.data}
          range={range}
          showFormer={showFormer}
          onOpenCustomerBilling={onOpenCustomerBilling}
        />
      )}
    </div>
  );
}

function FleetRevenue({
  screen,
  range,
  showFormer,
  onOpenCustomerBilling,
}: {
  screen: FleetRevenueScreen;
  range: DayRangeQuery | null;
  showFormer: boolean;
  onOpenCustomerBilling: (publicId: string) => void;
}) {
  const { customers, total } = screen;
  const rangeLabel = dayRangeLabel(screen.range);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base">Customers</CardTitle>
          <CardDescription>
            {customers.length === 1 ? '1 customer' : `${customers.length} customers`} · every
            figure, the total and the export are for {rangeLabel}.
          </CardDescription>
        </div>
        <ExportButton range={range} showFormer={showFormer} rangeLabel={rangeLabel} />
      </CardHeader>
      <CardContent className="p-0">
        {customers.length === 0 ? (
          // An estate with nobody in it is a LOADED screen with nothing in it,
          // which is the state a fresh deployment is in.
          <p className="px-6 py-8 text-sm text-muted-foreground">No customers are registered.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Usage events</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead>Settlement</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Billing page</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((row) => (
                  <CustomerRow key={row.customer.public_id} row={row} onOpenCustomerBilling={onOpenCustomerBilling} />
                ))}
              </TableBody>
              <TableFooter>
                <TotalRow total={total} />
              </TableFooter>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** One customer's line: identity, status, the figures, and the way to their Billing page. */
function CustomerRow({
  row,
  onOpenCustomerBilling,
}: {
  row: FleetRevenueCustomer;
  onOpenCustomerBilling: (publicId: string) => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{row.customer.display_name}</div>
        <div className="font-mono text-xs text-muted-foreground">{row.customer.public_id}</div>
      </TableCell>
      <TableCell>
        {/* The same Status column All Customers draws. A superseded row's
            successor opens the way this row's own Billing button does — into
            the successor's Billing page — since that is where its figure went. */}
        <StatusCell retired={row.retired} supersededBy={row.superseded_by} onOpenCustomer={onOpenCustomerBilling} />
      </TableCell>
      <TableCell className="text-right tabular-nums">{countText(row.usage_events)}</TableCell>
      <TableCell className="text-right">
        {row.billable ? <AmountCell amount={row.amount} /> : <NotBillable />}
      </TableCell>
      <TableCell className="text-right">
        {row.billable ? <PendingCell pending={row.pending} /> : <NotBillable />}
      </TableCell>
      <TableCell>
        <SettlementCell settlement={row.settlement} carry={row.carry} withheld={row.withheld} />
      </TableCell>
      <TableCell className="text-right">
        <CostCell cost={row.cost} />
      </TableCell>
      <TableCell className="text-right">
        <MarginCell margin={row.margin} />
      </TableCell>
      <TableCell>
        {/* The scope switch, then the customer's own Billing page — the source
            of the figure on this row, so it can be checked against it. It
            leaves the metricsIQ surface, which is what the arrow says. */}
        <span className="inline-block" title="Act for this customer and open their Billing page">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Open ${row.customer.display_name}'s Billing page`}
            onPress={() => onOpenCustomerBilling(row.customer.public_id)}
          >
            Billing <ArrowUpRight className="size-3.5" />
          </Button>
        </span>
      </TableCell>
    </TableRow>
  );
}

/**
 * The footer: the figures that exist, summed on the service.
 *
 * Nothing is added here. The total amount is the pricing module's own total
 * over every customer's lines, so two currencies across customers withhold it
 * exactly as they would within one customer.
 */
function TotalRow({ total }: { total: FleetRevenueTotal }) {
  const customers = `${countText(total.customers_with_figure)} with a figure · ${countText(
    total.customers_not_billable,
  )} not billable`;
  return (
    <TableRow className="font-medium">
      <TableCell>
        <div>Total</div>
        <div className="text-xs font-normal text-muted-foreground">{customers}</div>
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell className="text-right">
        <AmountCell amount={total.amount} />
      </TableCell>
      <TableCell className="text-right">
        <PendingCell pending={total.pending} />
      </TableCell>
      <TableCell />
      <TableCell className="text-right">
        <CostCell cost={total.cost} />
      </TableCell>
      <TableCell className="text-right">
        <MarginCell margin={total.margin} />
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

/** The words where a figure would be, for a customer nothing here can bill. */
function NotBillable() {
  return (
    <span className="text-muted-foreground" title="No adapter is configured for this customer's channel">
      Not billable
    </span>
  );
}

/**
 * The estimate, with its mark; or the reason there is none.
 *
 * `null` is an em dash, never a zero, and the row says why beneath it: the
 * lines were in two currencies, or some could not be priced, or there was
 * simply nothing to price.
 */
function AmountCell({ amount }: { amount: FleetRevenueAmount }) {
  return (
    <div className="tabular-nums">
      {amount.amount === null ? (
        <div className="text-muted-foreground" title="No priced line in these days">
          {ABSENT}
        </div>
      ) : (
        <div className="flex items-baseline justify-end gap-1.5">
          <span className="font-medium">{money(amount.amount, amount.currency)}</span>
          <Badge variant="outline">Estimate</Badge>
        </div>
      )}
      {amount.mixed_currencies && (
        <div className="text-xs font-normal text-warning">Lines in more than one currency; no total</div>
      )}
      {amount.unpriced_lines > 0 && (
        <div className="text-xs font-normal text-warning">
          {amount.unpriced_lines} line{amount.unpriced_lines === 1 ? '' : 's'} not priced
        </div>
      )}
    </div>
  );
}

/** What the unanswered hours would come to, and how many there are. */
function PendingCell({ pending }: { pending: FleetRevenuePending }) {
  if (pending.records === 0) {
    return (
      <div className="tabular-nums text-muted-foreground" title="Every record in these days has an answer">
        {ABSENT}
      </div>
    );
  }
  const records = `${countText(pending.records)} record${pending.records === 1 ? '' : 's'} awaiting an answer`;
  return (
    <div className="tabular-nums">
      <div className="font-medium">{money(pending.amount, pending.currency)}</div>
      <div className="text-xs font-normal text-muted-foreground">{records}</div>
    </div>
  );
}

/**
 * One badge per channel, in the colour its state means.
 *
 * Pending wears the Billing page's accruing tone — the signal that something
 * has not settled. Not billable is muted. Carried forward names the remainder
 * beneath, per dimension, so the state is not a word without a figure, and
 * withheld names what was packaged and not sent, which is in no amount here.
 */
function SettlementCell({
  settlement,
  carry,
  withheld,
}: {
  settlement: FleetRevenueSettlement[];
  carry: FleetRevenueCarry[];
  withheld: FleetRevenueWithheld[];
}) {
  if (settlement.length === 0) {
    return <span className="text-muted-foreground">{ABSENT}</span>;
  }
  const carried = settlement.some((entry) => entry.state === 'carried_forward');
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center gap-1">
        {settlement.map((entry) => (
          <SettlementBadge key={entry.channel} entry={entry} />
        ))}
      </div>
      {carried &&
        carry.map((entry) => (
          <div key={entry.dimension} className="text-xs font-normal text-muted-foreground">
            {quantityText(entry.quantity)} <span className="font-mono">{entry.dimension}</span> carried
          </div>
        ))}
      {withheld.map((entry) => (
        <div key={`withheld-${entry.dimension}`} className="text-xs font-normal text-muted-foreground">
          {quantityText(entry.quantity)} <span className="font-mono">{entry.dimension}</span> withheld
        </div>
      ))}
    </div>
  );
}

function SettlementBadge({ entry }: { entry: FleetRevenueSettlement }) {
  const label = `${entry.channel} · ${STATE_LABEL[entry.state]}`;
  switch (entry.state) {
    case 'pending':
      return (
        <Badge variant="outline" className={ACCRUING_TONE}>
          {label}
        </Badge>
      );
    case 'not_billable':
      return <Badge variant="secondary">{label}</Badge>;
    case 'withheld':
      // Warning, not danger: nothing failed. Somebody decided this agreement
      // is not billed, and a reader adding up the column must see that.
      return (
        <Badge variant="outline" className={ACCRUING_TONE}>
          {label}
        </Badge>
      );
    default:
      return <Badge variant="outline">{label}</Badge>;
  }
}

/**
 * The customer's cost over the range, with how much of it was accounted for.
 *
 * The dash is for a customer none of whose instances posted a day in the
 * range. It is not a zero: a posted day with no cost gives a real $0.00 with
 * instance-days beside it, which is a figure resting on days accounted for.
 */
function CostCell({ cost }: { cost: FleetRevenueCost }) {
  const expected = cost.days_in_window * cost.instances;
  const posted = `${countText(cost.days_posted)} of ${countText(expected)} instance-day${
    expected === 1 ? '' : 's'
  } posted`;
  return (
    <div className="tabular-nums">
      {cost.usd === null ? (
        <div className="text-muted-foreground" title="No instance posted a day in this range">
          {ABSENT}
        </div>
      ) : (
        <div className="font-medium">{money(cost.usd, COST_CURRENCY)}</div>
      )}
      <div className="text-xs font-normal text-muted-foreground">{posted}</div>
    </div>
  );
}

/** Amount minus cost, as the service subtracted it; a dash where either half is absent. */
function MarginCell({ margin }: { margin: string | null }) {
  if (margin === null) {
    return (
      <span className="tabular-nums text-muted-foreground" title="A margin needs both an amount and a cost">
        {ABSENT}
      </span>
    );
  }
  return <span className="font-medium tabular-nums">{money(margin, COST_CURRENCY)}</span>;
}

/**
 * The CSV of the range on screen, fetched with the bearer and saved.
 *
 * Carries the same selection the screen was read with, so the file holds
 * exactly the customers and lines above it. A refusal is shown beside the
 * button in the service's words rather than swallowed.
 */
function ExportButton({
  range,
  showFormer,
  rangeLabel,
}: {
  range: DayRangeQuery | null;
  showFormer: boolean;
  rangeLabel: string;
}) {
  const { authorizedFetch } = usePortalSession();
  const [state, setState] = useState<{ busy: boolean; failed: string | null }>({
    busy: false,
    failed: null,
  });

  const download = () => {
    setState({ busy: true, failed: null });
    downloadFleetRevenueExport(authorizedFetch, range, showFormer)
      .then(() => setState({ busy: false, failed: null }))
      .catch((failure: unknown) =>
        setState({
          busy: false,
          failed:
            failure instanceof PortalRequestFailed
              ? failure.message
              : 'The export could not be downloaded. Check your connection and try again.',
        }),
      );
  };

  return (
    <div className="shrink-0 text-right">
      <Button variant="outline" size="sm" isDisabled={state.busy} onPress={download}>
        <Download className="size-4" /> Export CSV
      </Button>
      <div className="mt-1 text-xs text-muted-foreground">
        {state.failed ?? `${rangeLabel}, one row per customer, channel and priced line`}
      </div>
    </div>
  );
}
